import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import Spinner from 'app/components/Spinner'
import { useAuthStore } from 'app/stores/StoreContext'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { useStickyLocationSync } from 'app/hooks/useStickyLocation'
import { debug, logError, logWarn } from 'lib/logging'

/**
 * Gate that resolves workspace context before rendering children.
 * Reads `wId` from URL, compares with the user's current workspace,
 * and triggers a workspace switch (+ full reload) if they differ.
 *
 * Cross-org deep links work because the API's switchWorkspace mutation
 * auto-switches a super user's org to match the target workspace
 * (mirrors legacy userService.setWorkspace). Children never render
 * until workspace context is correct.
 */
// Bounds a workspace switch to a single attempt per target wId across the
// reload boundary. A component ref resets on `window.location.reload()`, so
// if the reloaded session still doesn't reflect the switch (a cookie-commit
// race), sessionStorage is what lets us detect "we already tried this" and
// avoid looping forever.
const SWITCH_ATTEMPT_KEY = `trk:wsSwitchAttempt`

export const WorkspaceGate = observer(function WorkspaceGate({ children }: { children: React.ReactNode }) {
  const auth = useAuthStore()
  const { user } = auth
  const location = useLocation()
  const navigate = useNavigate()
  const isOrgUser = useIsOrgUser()
  const switchingRef = useRef(false)
  const [resolved, setResolved] = useState(false)
  // Lives here rather than in AppLayout because it must not touch the URL
  // until the workspace question above is settled.
  useStickyLocationSync(resolved)

  // Reads the live URL, not this render's location: a child route's <Navigate> fires its
  // effect before this one in the same commit, and resolving against the stale path would
  // undo that redirect and strand the page blank.
  const replaceWorkspaceId = (workspaceId: string | undefined) => {
    const { pathname, search, hash } = window.location
    const next = new URLSearchParams(search)
    next.set(`wId`, workspaceId || `null`)
    navigate({ pathname, search: `?${next}`, hash }, { replace: true })
  }

  // Resolve workspace context
  useEffect(() => {
    if (!user || switchingRef.current) return

    const currentWId = new URL(window.location.href).searchParams.get(`wId`)

    // No wId — stamp current workspace and proceed
    if (!currentWId) {
      replaceWorkspaceId(user.workspaceId)
      setResolved(true)
      return
    }

    const needsSwitch =
      (currentWId === `null` && user.workspaceId) || (currentWId.length > 4 && currentWId !== user.workspaceId)

    // wId matches — proceed
    if (!needsSwitch) {
      sessionStorage.removeItem(SWITCH_ATTEMPT_KEY)
      setResolved(true)
      return
    }

    // Non-org users can't switch — fix URL and proceed
    if (!isOrgUser) {
      replaceWorkspaceId(user.workspaceId)
      sessionStorage.removeItem(SWITCH_ATTEMPT_KEY)
      setResolved(true)
      return
    }

    // Already attempted a switch to this exact target across a reload and
    // came back still mismatched (switchWorkspace cookie-commit race). Don't
    // switch again — render the current workspace instead of spinning forever.
    if (sessionStorage.getItem(SWITCH_ATTEMPT_KEY) === currentWId) {
      sessionStorage.removeItem(SWITCH_ATTEMPT_KEY)
      logWarn(`WorkspaceGate: repeated switch to ${currentWId} did not take effect, rendering current workspace`)
      setResolved(true)
      return
    }

    // Org user with mismatched wId — switch workspace.
    // For super users, the API derives and switches the org from the workspace.
    switchingRef.current = true
    const targetId = currentWId === `null` ? null : currentWId
    debug(`workspace`, `WorkspaceGate: switching to ${targetId} (current: ${user.workspaceId})`)

    auth
      .switchWorkspace(targetId)
      .then((result) => {
        if (result.error) {
          logError(`WorkspaceGate: switch failed`, result.error.message)
          replaceWorkspaceId(user.workspaceId)
          sessionStorage.removeItem(SWITCH_ATTEMPT_KEY)
          switchingRef.current = false
          setResolved(true)
          return
        }
        // Record the attempt before reloading so it survives the reload —
        // this is what lets us detect a repeated switch to the same target.
        sessionStorage.setItem(SWITCH_ATTEMPT_KEY, currentWId)
        // Full reload to clear all cached data
        window.location.reload()
      })
      .catch((err) => {
        logError(`WorkspaceGate: switch failed`, err)
        sessionStorage.removeItem(SWITCH_ATTEMPT_KEY)
        switchingRef.current = false
        setResolved(true)
      })
  }, [user, isOrgUser, location.pathname, location.search])

  if (!resolved) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <Spinner size={10} />
      </div>
    )
  }

  return <>{children}</>
})
